/* Crisp, Poppy's in-app support chat, through its $crisp command queue (docs.crisp.chat, Web
 * SDK). The queue exists from loadSupportChat on, so commands pushed before the script arrives
 * are replayed, in order, once it has. Rolling back to another provider is a git revert. */

declare global {
  interface Window {
    /** A plain array until the script loads, then Crisp's own object. Both push. */
    $crisp?: { push: (command: unknown[]) => unknown };
    CRISP_WEBSITE_ID?: string;
    /** Private server-issued credential for restoring the user's support conversation. */
    CRISP_TOKEN_ID?: string;
  }
}

/** Crisp's public website id; a deploy can point at another workspace. */
export const CRISP_WEBSITE_ID =
  process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID?.trim() || '51cffd0e-bf09-4881-a377-55ef6e4d3a81';

export const CRISP_SCRIPT_URL = 'https://client.crisp.chat/l.js';

// Lives with the SDK, rather than a React mount, so remounts cannot inherit another account.
let boundUserId: string | null = null;

/** The html class board chrome suppression sets (useBoardChromeSuppressed). */
const CHROME_SUPPRESSED_CLASS = 'board-node-fullscreen';

const isChromeSuppressed = () =>
  document.documentElement.classList.contains(CHROME_SUPPRESSED_CLASS);

/** Whether the chat is on this page. Callers with a fallback (mailto) check first. */
export const isSupportChatLoaded = () => typeof window !== 'undefined' && !!window.$crisp;

export const isSupportChatBoundTo = (userId: string) =>
  isSupportChatLoaded() && boundUserId === userId;

/** Queues a Crisp command; dropped when the chat was never loaded on this page. */
export function crisp(...command: unknown[]): void {
  if (isSupportChatLoaded()) window.$crisp!.push(command);
}

/**
 * Binds the chatbox to a private server-issued token (Crisp "session continuity") so a user
 * keeps one conversation across page loads, tabs and devices — even if cookies are cleared or the
 * preview domain won't persist them. Crisp reads `CRISP_TOKEN_ID` only when the loader runs, so
 * this must be set before `loadSupportChat`; changing it afterwards needs a `session:reset`.
 * Passing null clears it, so the next (signed-out) visitor starts a fresh anonymous session.
 */
export function setSupportChatToken(tokenId: string | null): void {
  if (typeof window === 'undefined') return;
  if (tokenId) window.CRISP_TOKEN_ID = tokenId;
  else delete window.CRISP_TOKEN_ID;
}

/** Loads once, or switches the existing SDK session before any new identity is sent. */
export function loadSupportChat(tokenId: string | null = null, userId: string | null = null): void {
  if (typeof window === 'undefined') return;
  if (isSupportChatLoaded()) {
    if (boundUserId === userId && (window.CRISP_TOKEN_ID ?? null) === tokenId) return;
    boundUserId = userId;
    setSupportChatToken(tokenId);
    if (Array.isArray(window.$crisp)) {
      // The loader has not arrived yet. Drop the previous account's queued identity/draft
      // before it can be replayed into the new account's session. Keep SDK configuration.
      const configuration = window.$crisp.filter(command =>
        ['safe', 'on', 'off', 'config'].includes(command[0])
      );
      window.$crisp.splice(0, window.$crisp.length, ...configuration);
    }
    crisp('do', 'chat:close');
    crisp('set', 'message:text', ['']);
    crisp('do', 'session:reset');
    return;
  }
  boundUserId = userId;
  // Set before the script is injected: Crisp only reads the token as the loader initializes.
  setSupportChatToken(tokenId ?? null);
  const queue: unknown[][] = [];
  window.$crisp = queue;
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  // Safe mode: an unsupported command is logged instead of thrown into the page.
  crisp('safe', true);
  // A saved cookie may belong to a previous account. Only a private token can restore it.
  if (!tokenId) crisp('do', 'session:reset');
  // Opened over suppressed chrome (openSupportChat), the chat hides again once it closes.
  crisp('on', 'chat:closed', () => {
    if (isChromeSuppressed()) crisp('do', 'chat:hide');
  });
  // Chrome can already be suppressed before the chat loads: the vault always is.
  if (isChromeSuppressed()) crisp('do', 'chat:hide');

  const script = document.createElement('script');
  script.src = CRISP_SCRIPT_URL;
  script.async = true;
  document.head.appendChild(script);
}

/** Opens the chat, past a hidden chatbox too, with an optional draft in the composer. */
export function openSupportChat(message?: string): void {
  if (message) crisp('set', 'message:text', [message]);
  crisp('do', 'chat:show');
  crisp('do', 'chat:open');
}

/** Board chrome suppression: hides the whole chatbox. openSupportChat still opens it. */
export function setSupportChatHidden(hidden: boolean): void {
  if (!isSupportChatLoaded()) return;
  crisp('do', hidden || isChromeSuppressed() ? 'chat:hide' : 'chat:show');
}
